<template>
  <div class="ranking">
    <div class="title">
      {{ $t('monopoly.ranking') }}
    </div>
    <div class="table">
      <div class="thead">
        <div class="tr">
          <div class="td rank">
            {{ $t('monopoly.rankingTable.rank') }}
          </div>
          <div class="td nickname">
            {{ $t('monopoly.rankingTable.nickname') }}
          </div>
          <div class="td laps">
            {{ $t('monopoly.rankingTable.laps') }}
          </div>
          <div class="td time">
            {{ $t('monopoly.rankingTable.time') }}
          </div>
        </div>
      </div>
      <div class="tbody">
        <div
          v-for="item in rankingList"
          :key="item.rank"
          class="tr"
          :class="{
            'rank-1st': item.rank === 1,
            'rank-2nd': item.rank === 2,
            'rank-3rd': item.rank === 3,
            empty: item.empty,
          }"
        >
          <div class="td rank">
            {{ item.rank }}
          </div>
          <div class="td nickname">
            {{ item.empty ? $t('monopoly.rankingEmptyNickname') : (item.nickname || item.accountId || '-') }}
          </div>
          <div class="td laps">
            {{ item.empty ? '-' : item.totalLaps }}
          </div>
          <div class="td time">
            {{ item.empty ? '-' : formatReachTime(item.lapReachTime) }}
          </div>
        </div>
      </div>
    </div>
    <div class="rewards">
      <div
        v-for="(reward, index) in rankRewardList"
        :key="reward.rewardCode || index"
        class="reward-item"
        :class="rewardItemClass(index)"
      >
        <div class="reward-title">
          {{ formatRewardTitle(reward) }}
        </div>
        <div class="reward-content">
          <div
            v-for="(prop, pIndex) in getRewardDisplayItems(reward)"
            :key="`${reward.rewardCode}-${prop.propId || pIndex}`"
            :class="pIndex === 0 ? 'prop-1' : 'prop-2'"
          >
            <div class="icon-wrap">
              <img
                v-if="prop.propId"
                class="icon"
                :src="getPropIcon(prop.propId)"
                :alt="prop.propId"
              >
            </div>
            <div class="text">
              <template v-for="(part, partIndex) in splitTextBySpace(prop.text)" :key="partIndex">
                <span v-if="part.trim()" class="text-seg">{{ part }}</span>
                <template v-else>{{ part }}</template>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import MonopolyApi from '@/views/act/monopoly/api'
import { getPropIcon, formatPropText, splitTextBySpace } from '@/views/act/monopoly/utils/prop'

dayjs.extend(advancedFormat)

const TOP_N = 10

const { t } = useI18n()
const commonConfig = inject('monopolyCommonConfig', null)
const registerRankListRefresh = inject('registerMonopolyRankListRefresh', null)

const rawList = ref([])

const rankingList = computed(() => {
  const map = new Map()
  rawList.value.forEach((item) => {
    if (item?.rank) map.set(Number(item.rank), item)
  })
  return Array.from({ length: TOP_N }, (_, index) => {
    const rank = index + 1
    const item = map.get(rank)
    if (!item) {
      return {
        rank,
        empty: true,
        nickname: '',
        accountId: '',
        totalLaps: 0,
        lapReachTime: 0,
      }
    }
    return {
      rank,
      empty: false,
      nickname: item.nickname || '',
      accountId: item.accountId || '',
      totalLaps: item.totalLaps ?? 0,
      lapReachTime: item.lapReachTime || 0,
    }
  })
})

const rankRewardList = computed(() => {
  const list = commonConfig?.rankRewards || []
  return [...list].sort((a, b) => (a.rankMin || 0) - (b.rankMin || 0))
})

const rewardItemClass = (index) => {
  if (index === 0) return 'reward-1st'
  if (index === 1) return 'reward-2nd'
  return 'reward-3rd'
}

const formatRewardTitle = (reward) => {
  const min = Number(reward.rankMin || 0)
  const max = Number(reward.rankMax || min)
  if (min === max) {
    return t('monopoly.rankingRewardTitleSingle', { rank: min })
  }
  return t('monopoly.rankingRewardTitleRange', { min, max })
}

const getRewardDisplayItems = (reward) => {
  const items = []
  ;(reward?.props || []).forEach((prop) => {
    if (!prop?.propId && !prop?.propName) return
    items.push({
      propId: prop.propId || '',
      text: formatPropText(t, prop),
    })
  })
  // 配置返回顺序与设计左右位置相反，前端交换展示
  return items.reverse()
}

const formatReachTime = (ms) => {
  const value = Number(ms || 0)
  if (!value) return '-'
  // 需求格式：18:00 9th July（UTC+7）
  return dayjs(value).utcOffset(7).format('HH:mm Do MMMM')
}

const loadRankList = async () => {
  try {
    const res = await MonopolyApi.getRankList()
    rawList.value = res?.list || []
  } catch (e) {
    console.error(e)
    rawList.value = []
  }
}

onMounted(() => {
  registerRankListRefresh?.(loadRankList)
  loadRankList()
})

onUnmounted(() => {
  registerRankListRefresh?.(null)
})
</script>

<style lang="less" scoped>
.ranking {
  width: 750px;
  height: 1728px;
  padding-left: 6px;
  background: url('~@/views/act/monopoly/assets/img/ranking.png') no-repeat center center / 100% 100%;
  margin-top: 80px;
  .title {
    height: 120px;
    line-height: 120px;
    text-align: center;
    color: #fff;
    font-size: 50px;
  }
  .table {
    width: 744px;
    margin-top: 8px;
    padding: 0 15px;
    .thead, .tbody {
      .tr {
        display: flex;
        gap: 5px;
        padding: 0 10px;
        .td {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          &.rank {
            width: 80px;
          }
          &.nickname {
            width: 230px;
          }
          &.laps {
            width: 140px;
          }
          &.time {
            flex: 1;
          }
          font-size: 24px;
          color: #2A3758;
          text-align: center;
        }
      }
    }
    .thead {
      .tr {
        height: 75px;
      }
    }
    .tbody {
      margin-top: 5px;
      display: flex;
      flex-direction: column;
      gap: 2px;
      .tr {
        height: 56px;
        border: 2px solid #98EEFE;
        border-radius: 16px;
        &.rank-1st {
          border: 2px solid #FFF97D;
          background: #FFF97D;
        }
        &.rank-2nd {
          border: 2px solid #77CCFF;
          background: #77CCFF;
        }
        &.rank-3rd {
          border: 2px solid #E5BB7B;
          background: #E5BB7B;
        }
        &.empty {
          .td {
            color: #7a8aa3;
          }
        }
      }
    }
  }

  .rewards {
    margin: 0 auto;
    width: 710px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    background: rgba(121, 230, 240, 0.3);
    padding: 30px 35px;
    border-radius: 25px;
    margin-top: 8px;
    .reward-item {
      width: 642px;
      height: 272px;
      background: url('~@/views/act/monopoly/assets/img/reward-bg-1.png') no-repeat center center / 100% 100%;
      .reward-title {
        height: 70px;
        line-height: 70px;
        text-align: center;
        font-size: 30px;
        color: #B3730D;
      }
      .reward-content {
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 50px;
        padding-bottom: 10px;
        .prop-1,
        .prop-2 {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          .icon-wrap {
            .icon {
              height: 100px;
              object-fit: contain;
              display: block;
            }
          }
          .text {
            font-size: 24px;
            color: #303030;
            text-align: center;
            max-width: 250px;
            line-height: 1.3;
            word-break: normal;
            overflow-wrap: normal;
            .text-seg {
              display: inline-block;
              white-space: nowrap;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              vertical-align: bottom;
            }
          }
        }
        .prop-2 {
          .icon-wrap {
            .icon {
              height: 120px;
            }
          }
        }
      }
      &.reward-1st {
        background: url('~@/views/act/monopoly/assets/img/reward-bg-1.png') no-repeat center center / 100% 100%;
      }
      &.reward-2nd {
        background: url('~@/views/act/monopoly/assets/img/reward-bg-2.png') no-repeat center center / 100% 100%;
        .reward-title {
          color: #1B6ACC;
        }
      }
      &.reward-3rd {
        background: url('~@/views/act/monopoly/assets/img/reward-bg-3.png') no-repeat center center / 100% 100%;
        color: #944510;
        .reward-title {
          color: #944510;
        }
      }
    }
  }
}
</style>
